import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { sidebarQueryKeys } from '../api/queryKeys'
import { recordTableOpened } from '../api/tableOrganization.api'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import { useTableOrganization } from './useTableOrganization'
import type { TableSidebarOrganizationResponse } from '../api/contracts'

const TABLE_PAGE_PATH =
  /^\/tables\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i

/** The table a path belongs to: any page under `/tables/:tableId`. */
export function tableIdFromPath(pathname: string): string | null {
  return TABLE_PAGE_PATH.exec(pathname)?.[1] ?? null
}

/** Whether the sidebar's loaded table list has `tableId`. */
export function isTableListed(
  data: TableSidebarOrganizationResponse | undefined,
  tableId: string | null
): boolean {
  return Boolean(tableId && data?.tables.some((table) => table.id === tableId))
}

/**
 * Marks `tableId` as just opened. The stamp is the device's time, or just
 * after the newest stamp already there when the device clock runs behind the
 * server's, so the table always comes first under Recency.
 */
export function stampTableOpened(
  data: TableSidebarOrganizationResponse | undefined,
  tableId: string,
  now: number = Date.now()
): TableSidebarOrganizationResponse | undefined {
  if (!data || !data.tables.some((table) => table.id === tableId)) return data
  const newest = data.tables.reduce((latest, table) => {
    const time = table.last_opened_at ? Date.parse(table.last_opened_at) : NaN
    return Number.isFinite(time) && time > latest ? time : latest
  }, Number.NEGATIVE_INFINITY)
  const stamp = new Date(Math.max(now, newest + 1)).toISOString()
  return {
    ...data,
    tables: data.tables.map((table) =>
      table.id === tableId ? { ...table, last_opened_at: stamp } : table
    ),
  }
}

/**
 * Records each table the user opens, from any page of it, so the sidebar's
 * Recency sort can put it first, and tells the sidebar which table is open so
 * only that row's highlight changes. The sidebar cache is stamped once, right
 * away; the server keeps its own time for the next load.
 *
 * Only a table the sidebar lists is recorded, once its list has loaded: an
 * address for a table that isn't there (or that the user can't read) has
 * nothing to sort, and the server would refuse the stamp.
 */
export function useRecordTableOpens() {
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const tableId = tableIdFromPath(pathname)
  const recordedTableIdRef = useRef<string | null>(null)
  const setActiveTableId = useGlobalSidebarStore((s) => s.setActiveTableId)
  const { data: organization } = useTableOrganization()
  const isListed = isTableListed(organization, tableId)

  // Before paint, so the highlight moves in the same frame as the page.
  useLayoutEffect(() => {
    setActiveTableId(tableId)
  }, [tableId, setActiveTableId])

  useEffect(() => {
    if (!tableId || !isListed) {
      // Leaving the table (or never finding it) means coming back is a new open.
      recordedTableIdRef.current = null
      return
    }
    if (tableId === recordedTableIdRef.current) return
    recordedTableIdRef.current = tableId

    queryClient.setQueryData<TableSidebarOrganizationResponse>(
      sidebarQueryKeys.tableOrganization,
      (data) => stampTableOpened(data, tableId)
    )
    recordTableOpened(tableId).catch(() => {
      // Recency is a convenience; a failed stamp keeps the local one and the
      // next open tries again.
    })
  }, [tableId, isListed, queryClient])
}
